import { useQuery } from '@tanstack/react-query';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import { DriveSweepModel } from '../../../../types';
import SimpleDrawer from '../../../shared/simple-drawer';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import AvatarChip from '../../../shared/avatar-chip';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import DeleteDriveSweep from './delete-drive-sweep-dialog';
import EditDriveSweepDialog from './edit-drive-sweep/edit-drive-sweep-dialog';
import DisableDriveSweepDialog from './disable-drive-sweep-dialog';
import EnableDriveSweepDialog from './enable-drive-sweep-dialog';
import DriveSweepLogsGrid from './drive-sweep-logs-grid';
import { useDriveSweepActions } from './use-drive-sweep-actions';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { getAllDriveMonitors, UserKeys } from '../../../api/users-api';
import React, { useMemo, useState } from 'react';
import StatusLabel from '../../../shared/status-label';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreVert from '@mui/icons-material/MoreVert';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Update from '@mui/icons-material/Update';

interface DriveSweepDetailsDrawerOldProps {
  open: boolean;
  handleClose: () => void;
  selectedDriveSweep: DriveSweepModel | null;
}

export default function DriveSweepDetailsDrawerOld({
  open,
  handleClose,
  selectedDriveSweep,
}: Readonly<DriveSweepDetailsDrawerOldProps>) {
  const { driveRoles, changeLogRoles } = useUserSettings();

  const {
    handleStartDriveSweep,
    handleRunWithChangeLog,
    isStartingSweep,
    openChangeLogDialog,
    setOpenChangeLogDialog,
    changeLogMessage,
    setChangeLogMessage,
    setDriveSweepToStart,
  } = useDriveSweepActions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEnable, setOpenEnable] = useState(false);
  const [openDisable, setOpenDisable] = useState(false);

  const { data: sweepData } = useQuery({
    queryKey: [UserKeys.DriveSweepModels],
    queryFn: getAllDriveMonitors,
    enabled: !!selectedDriveSweep,
  });
  const currentSweep =
    sweepData?.find((s) => s.folderId === selectedDriveSweep?.folderId) ??
    selectedDriveSweep;

  const canEdit = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanEditDriveSweep.value
  );

  const canStart = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanStartDriveSweep.value
  );

  const canRun = canStart && !currentSweep?.isRunning;

  const canToggle = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.UpdateDriveSweepStatus.value
  );
  const canDelete = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanDeleteDriveSweep.value
  );

  const runDisabledReason = !canStart
    ? Messages.Errors.Permission.Acting(DriveRoles.CanStartDriveSweep.name)
    : currentSweep?.isRunning
    ? 'Sweep is currently running'
    : undefined;
  const editDisabledReason = canEdit
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.CanEditDriveSweep.name);
  const toggleDisabledReason = canToggle
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.UpdateDriveSweepStatus.name);
  const deleteDisabledReason = canDelete
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.CanDeleteDriveSweep.name);

  const configData: Record<string, React.ReactNode> = useMemo(() => {
    if (!currentSweep) return {};

    const data: Record<string, React.ReactNode> = {
      Folder: currentSweep.folderName ? (
        <AvatarChip
          type="User Drive"
          email={currentSweep.folderName}
          size="small"
        />
      ) : (
        'N/A'
      ),
      Owner: <AvatarChip email={currentSweep.ownerId} type="User" />,
      'Created By': <AvatarChip email={currentSweep.createdBy} type="User" />,
    };

    if (currentSweep.lastUpdated) {
      data['Last Updated Date'] = new Date(
        currentSweep.lastUpdated
      ).toLocaleString();
    }

    if (currentSweep.updatedBy) {
      data['Last Updated By'] = (
        <AvatarChip email={currentSweep.updatedBy} type="User" />
      );
    }

    return data;
  }, [currentSweep]);

  function getStatusChip(sweep: DriveSweepModel) {
    const status = sweep.isRunning
      ? 'INPROGRESS'
      : sweep.isActive
      ? 'ENABLED'
      : 'DISABLED';
    return <StatusLabel status={status} />;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', md: '45%' },
        },
      }}
    >
      {selectedDriveSweep && currentSweep && (
        <SimpleDrawer title="Drive Sweep Details" onClose={handleClose}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {currentSweep.name}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {getStatusChip(currentSweep)}
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVert />
              </IconButton>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {currentSweep.desc || 'No description provided'}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{ mb: 2, alignItems: 'center' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'text.secondary' }}
            >
              <Update fontSize="small" />
              <Typography variant="body2" color="inherit">
                Last Run:{' '}
                {currentSweep.lastRun
                  ? new Date(currentSweep.lastRun).toLocaleString()
                  : 'Never'}
              </Typography>
            </Stack>
            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
              •
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'text.secondary' }}
            >
              <CalendarToday fontSize="small" />
              <Typography variant="body2" color="inherit">
                Created: {new Date(currentSweep.created).toLocaleString()}
              </Typography>
            </Stack>
          </Stack>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_e, val) => setActiveTab(val)}>
              <Tab label="Overview" />
              <Tab label="Logs" />
            </Tabs>
          </Box>

          <Box sx={{ pt: 1, pb: 4 }}>
            {activeTab === 0 && (
              <ModelDetailsDisplay title="Configuration" data={configData} />
            )}
            {activeTab === 1 && (
              <DriveSweepLogsGrid folderId={currentSweep.folderId} />
            )}
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
          >
            <DisabledTooltipMenuItem
              disabled={!canRun}
              data-disabled-reason={runDisabledReason}
              onClick={() => {
                setAnchorEl(null);
                handleStartDriveSweep(currentSweep, changeLogRoles);
              }}
            >
              <ListItemIcon>
                <PlayArrow fontSize="small" />
              </ListItemIcon>
              Run
            </DisabledTooltipMenuItem>
            <DisabledTooltipMenuItem
              disabled={!canEdit}
              data-disabled-reason={editDisabledReason}
              onClick={() => {
                setAnchorEl(null);
                setOpenEdit(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              Edit
            </DisabledTooltipMenuItem>
            {currentSweep.isActive ? (
              <DisabledTooltipMenuItem
                disabled={!canToggle}
                data-disabled-reason={toggleDisabledReason}
                onClick={() => {
                  setAnchorEl(null);
                  setOpenDisable(true);
                }}
              >
                <ListItemIcon>
                  <Cancel fontSize="small" />
                </ListItemIcon>
                Disable
              </DisabledTooltipMenuItem>
            ) : (
              <DisabledTooltipMenuItem
                disabled={!canToggle}
                data-disabled-reason={toggleDisabledReason}
                onClick={() => {
                  setAnchorEl(null);
                  setOpenEnable(true);
                }}
              >
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                Enable
              </DisabledTooltipMenuItem>
            )}
            <DisabledTooltipMenuItem
              disabled={!canDelete}
              data-disabled-reason={deleteDisabledReason}
              onClick={() => {
                setAnchorEl(null);
                setOpenDelete(true);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              Delete
            </DisabledTooltipMenuItem>
          </Menu>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => {
              setOpenChangeLogDialog(false);
              setDriveSweepToStart(null);
            }}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={handleRunWithChangeLog}
            mutateLoading={isStartingSweep}
          />

          {openEdit && (
            <EditDriveSweepDialog
              open={openEdit}
              handleClose={() => setOpenEdit(false)}
              selectedDriveSweep={currentSweep}
              changeLogRoles={changeLogRoles}
            />
          )}

          {openDelete && (
            <DeleteDriveSweep
              open={openDelete}
              handleClose={() => setOpenDelete(false)}
              selectedDriveSweeps={[currentSweep]}
              changeLogRoles={changeLogRoles}
              onSuccess={handleClose}
            />
          )}

          {openEnable && (
            <EnableDriveSweepDialog
              open={openEnable}
              handleClose={() => setOpenEnable(false)}
              selectedDriveSweeps={[currentSweep]}
              setSelectedRows={() => {}}
              changeLogRoles={changeLogRoles}
            />
          )}

          {openDisable && (
            <DisableDriveSweepDialog
              open={openDisable}
              handleClose={() => setOpenDisable(false)}
              selectedDriveSweeps={[currentSweep]}
              setSelectedRows={() => {}}
              changeLogRoles={changeLogRoles}
            />
          )}
        </SimpleDrawer>
      )}
    </Drawer>
  );
}
